import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Formik, Form } from 'formik';
import {
  BookingFormType,
  BookingType,
  FormBookingType,
  UpdateBookingType,
} from '../../../redux/types/bookingTypes';
import {
  BookingSelectors,
  removeBooking,
  setBookingList,
  updateCurrentBooking,
  updateViewedStatus,
} from '../../../redux/reducers/bookingSlice';
import BookingContactBlock from '../BookingContactBlock';
import BookingHeader from '../BookingHeader';
import BookingObjectBlock from '../BookingObjectBlock';
import BookingPersonBlock from '../BookingPersonBlock';
import BookingCommentBlock from '../BookingCommentBlock';

import styles from './BookingContainer.module.scss';
import { setModalWindowData } from '../../../redux/reducers/pageSlice';
import { ButtonType, ModalWindowTypes } from '../../../utils/@globalTypes';
import Button from '../../Button';
import useValidationSchema from '../validationSchema';
import ScrollToError from '../ScrollToError';
import { formatDate, isBookingSaveDisabled } from '../functions';
import { parsePhoneNumber } from '../../../utils/parsePhoneNumber';

type BookingContainerProps = {
  booking: BookingType;
  type: BookingFormType;
};

const BookingContainer = ({ booking, type }: BookingContainerProps) => {
  const dispatch = useDispatch();
  const validationSchema = useValidationSchema();
  const { check_out_date, id } = booking;

  const [isEdit, setIsEdit] = useState(false); // переключение формы между просмотром и редактированием
  const windowType = isEdit ? BookingFormType.edit : BookingFormType.view;

  const [isReadyToDelete, setIsReadyToDelete] = useState(false); //управление активностью кнопки "Удалить"

  const mealsCost =
    Number(booking.total_board_price) > 0 ? booking.total_board_price : '';
  const animalCost =
    Number(booking.total_animal_price) > 0 ? booking.total_animal_price : '';
  const livingCost =
    Number(booking.accommodation_price) > 0 ? booking.accommodation_price : '';
  const totalCost = (
    Number(booking.accommodation_price) +
    Number(booking.total_board_price) +
    Number(booking.total_animal_price)
  ).toFixed(2);
  const rest = (
    Number(booking.accommodation_price) +
    Number(booking.total_animal_price) +
    Number(booking.total_board_price) -
    Number(booking.paid)
  ).toFixed(2);

  const paid = Number(booking.paid) > 0 ? booking.paid : '';
  const initialPhone = (
    booking.phone.phone_code + booking.phone.phone_number
  ).slice(1);
  const initialValues: FormBookingType = useMemo(
    () => ({
      status: booking.status, //№1 в форме на мобиле
      rental_object: booking.rental_object,
      check_in_date: new Date(booking.check_in_date),
      check_out_date: new Date(booking.check_out_date),
      check_in_time: booking.check_in_time,
      check_out_time: booking.check_out_time,
      adult: booking.adult,
      children: booking.children,
      has_animals: booking.has_animals,
      animals_costs: animalCost,
      animals_count: booking.animals_count>0 ? booking.animals_count.toString() : "",
      animals_info: booking.animals_info || '',
      total: totalCost,
      paid: paid,
      meals_costs: mealsCost,
      living_costs: livingCost,
      rest: rest,
      last_name: booking.last_name,
      first_name: booking.first_name,
      second_name: booking.second_name || '',
      nationality: booking.nationality,
      email: booking.email,
      phone: initialPhone,
      telegram: booking.telegram || '',
      client_comment: booking.client_comment || '',
      owner_comment: booking.owner_comment || '',
      snapTotal:'',      
    }),
    [booking],
  );

  const isLast =
    moment(check_out_date).startOf('day').unix() <
    moment(new Date()).startOf('day').unix(); // является ли бронь прошедшей

  const allBooking = useSelector(BookingSelectors.getBookingList);
  useEffect(() => {
    //если бронь до этого не была просмотрена - ставим статус просмотренной
    if (!booking.viewed && allBooking) {
      dispatch(updateViewedStatus(id));
      const viewedBooking = { ...booking };
      viewedBooking.viewed = true;
      const newArr = allBooking.filter((item) => item.id !== id);
      newArr.push(viewedBooking);
      dispatch(setBookingList(newArr));
    }
  }, []);

  useEffect(() => {
    //если бронь прошедшая - сразу можно удалять
    if (isLast) {
      setIsReadyToDelete(true);
    }
  }, [isLast]);

  useEffect(() => {
    //переключение в режим редактирования
    if (type === BookingFormType.edit) {
      setIsEdit(true);
    }
  }, [type]);

  useEffect(() => {
    //если статус брони "отменена" - можно удалять
    if (booking.status === 'cld') {
      setIsReadyToDelete(true);
    }
  }, [booking.status]);

  const handleClickEdit = () => {
    setIsEdit(true);
  }; //переключение формы в режим редактирования

  const handleClickDelete = () => {
    dispatch(removeBooking(id));
  };
  const close = () => {
    dispatch(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      enableReinitialize={true}
      onSubmit={(values) => {
        if (windowType === BookingFormType.view) {
          return;
        }

        const updateBooking: UpdateBookingType = {};
        if (booking.rental_object !== values.rental_object) {
          updateBooking.rental_object = values.rental_object;
        }
        if (booking.status !== values.status) {
          updateBooking.status = values.status;
        }
        if (booking.check_in_date !== formatDate(values.check_in_date)) {
          updateBooking.check_in_date = formatDate(values.check_in_date);
        }
        if (booking.check_out_date !== formatDate(values.check_out_date)) {
          updateBooking.check_out_date = formatDate(values.check_out_date);
        }
        if (booking.check_in_time !== values.check_in_time) {
          updateBooking.check_in_time = values.check_in_time;
        }
        if (booking.check_out_time !== values.check_out_time) {
          updateBooking.check_out_time = values.check_out_time;
        }
        if (booking.adult !== values.adult) {
          updateBooking.adult = values.adult;
        }
        if (
          JSON.stringify(booking.children) !== JSON.stringify(values.children)
        ) {
          updateBooking.children = values.children;
        }
        if (booking.has_animals !== values.has_animals) {
          updateBooking.has_animals = values.has_animals;
        }
        if (booking.animals_info !== values.animals_info) {
          updateBooking.animals_info = values.animals_info;
        }
        if (
          booking.accommodation_price !==
          Number(values.living_costs).toFixed(2).toString()
        ) {
          updateBooking.accommodation_price = Number(values.living_costs)
            .toFixed(2)
            .toString();
        }
        if (
          booking.total_animal_price !==
          Number(values.animals_costs).toFixed(2).toString()
        ) {
          updateBooking.total_animal_price = Number(values.animals_costs)
            .toFixed(2)
            .toString();
        }
        if (booking.animals_count !== Number(values.animals_count)) {
          updateBooking.animals_count = Number(values.animals_count);
        }
        if (
          booking.total_board_price !==
          Number(values.meals_costs).toFixed(2).toString()
        ) {
          updateBooking.total_board_price = Number(values.meals_costs)
            .toFixed(2)
            .toString();
        }
        if (booking.paid !== Number(values.paid).toFixed(2).toString()) {
          updateBooking.paid = Number(values.paid).toFixed(2).toString();
        }
        if (booking.last_name !== values.last_name) {
          updateBooking.last_name = values.last_name;
        }
        if (booking.first_name !== values.first_name) {
          updateBooking.first_name = values.first_name;
        }
        if (booking.second_name !== values.second_name) {
          updateBooking.second_name = values.second_name;
        }
        if (booking.nationality !== values.nationality) {
          updateBooking.nationality = values.nationality;
        }
       
        if (booking.email !== values.email) {
          updateBooking.email = values.email;
        }
        if (initialValues.phone !== values.phone) {
          const newPhone = parsePhoneNumber(values.phone);
          updateBooking.phone = newPhone;
        }
        if (booking.telegram !== values.telegram) {
          updateBooking.telegram = values.telegram;
        }
        if (booking.client_comment !== values.client_comment) {
          updateBooking.client_comment = values.client_comment;
        }
        if (booking.owner_comment !== values.owner_comment) {
          updateBooking.owner_comment = values.owner_comment;
        }

        const keys = Object.keys(updateBooking).length;
        if (keys > 0) {
          try {
            dispatch(updateCurrentBooking({ id: id, data: updateBooking }));
          } catch (error) {
            console.error('Ошибка отправки:', error);
          }
        }
      }}
    >
      {(formik) => (
        <Form>
          <BookingHeader
            windowType={windowType}
            handleClickEdit={handleClickEdit}
            handleClickDelete={handleClickDelete}
            isLast={isLast}
            isReadyToDelete={isReadyToDelete}
            formik={formik}
            windowClose={close}
          />
          <main className={styles.mainContainer}>
            <BookingObjectBlock
              windowType={windowType}
              formik={formik}
              isLast={isLast}
              booking={booking}
            />
            <BookingPersonBlock windowType={windowType} formik={formik} />
            <BookingContactBlock windowType={windowType} formik={formik} />
            <BookingCommentBlock windowType={windowType} formik={formik} />
            <div className={styles.btnSave}>
              <Button
                title="Сохранить"
                disabled={isBookingSaveDisabled(
                  windowType,
                  formik.dirty,
                  formik.errors,
                )}
                type={ButtonType.PRIMARY_SMALL}
                actionType="submit"
              />
            </div>
          </main>
          <ScrollToError />
        </Form>
      )}
    </Formik>
  );
};

export default BookingContainer;
